import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, User } from 'lucide-react';
import { getRequest } from '../../apis/ApiInstence';
import headerBg from '../../Assets/img/9.jpg';

const ArticleCard = ({ article, idx }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.1 }}
      onMouseLeave={() => setIsExpanded(false)}
      className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl flex flex-col transition-all duration-500 border border-gray-100 min-w-[280px] sm:min-w-0 snap-center"
    >
      {/* IMAGE */}
      <Link to={`/news-media/${article.id}`} className="h-[200px] sm:h-[240px] overflow-hidden relative shrink-0 block">
        <img 
          src={article.image} 
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute top-4 left-4">
           <span className="bg-white/90 backdrop-blur-md text-[10px] font-bold text-title px-3 py-1 rounded-full uppercase tracking-widest">{article.category}</span>
        </div>
      </Link>

      {/* CONTENT */}
      <div className="p-6 md:p-8 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
          <Calendar size={12} className="text-orange-main" />
          {article.date}
        </div>

        <Link to={`/news-media/${article.id}`}>
          <h4 className="text-lg md:text-xl font-bold text-title mb-3 leading-tight group-hover:text-orange-main transition-colors line-clamp-2">
            {article.title}
          </h4>
        </Link>

        {/* Hover Description with See More/Less */}
        <div className="flex flex-col flex-1">
          <p className="text-sm md:text-base text-gray-500 leading-relaxed mb-4 line-clamp-3">
            {article.excerpt}
          </p>
        </div>

        <div className="flex items-center justify-between pt-6 border-t border-gray-50 mt-auto">
           <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                 <User size={14} className="text-gray-400" />
              </div>
              <span className="text-[11px] font-bold text-gray-600">{article.author}</span>
           </div>
           <Link to={`/news-media/${article.id}`} className="text-xs font-bold text-orange-main hover:underline uppercase tracking-widest">
             View Blog
           </Link>
        </div>
      </div>
    </motion.div>
  );
};

const NewsMedia = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 9;

  const assetBaseUrl = useMemo(() => {
    let base = process.env.REACT_APP_BACKEND || '';
    base = base.replace('/khajuraho/', '/');
    if (!base.endsWith('/')) {
      base = `${base}/`;
    }
    return base;
  }, []);

  const formatDate = (value) => {
    if (!value) return 'Recent';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Recent';
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  useEffect(() => {
    document.title = 'News & Media | GRAAHO A unit of Graahaam Builders';
    window.scrollTo(0, 0);

    const getArticles = async () => {
      setLoading(true);
      try {
        const endpointCandidates = [
          'blogs?page=1&limit=100',
          'blog?page=1&limit=100',
        ];

        let rows = [];
        for (const endpoint of endpointCandidates) {
          try {
            const response = await getRequest(endpoint);
            rows = Array.isArray(response?.data?.data) ? response.data.data : (Array.isArray(response?.data) ? response.data : []);
            if (rows.length > 0) break;
          } catch (error) {
            console.warn(`News fetch failed for endpoint: ${endpoint}`, error);
          }
        }

        const mappedArticles = rows.map((blog) => {
          let parsedImages = [];
          if (Array.isArray(blog.images)) {
            parsedImages = blog.images;
          } else if (typeof blog.images === 'string' && blog.images.trim().length > 0) {
            try {
              const maybeArray = JSON.parse(blog.images);
              parsedImages = Array.isArray(maybeArray) ? maybeArray : [blog.images];
            } catch (e) {
              parsedImages = [blog.images];
            }
          }

          const firstImage = parsedImages.length > 0 ? parsedImages[0] : '';
          const image = firstImage
            ? (firstImage.startsWith('http') ? firstImage : `${assetBaseUrl}${String(firstImage).replace(/^\/+/, '')}`)
            : 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2000&auto=format&fit=crop';

          const plainContent = String(blog.content || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

          return {
            id: blog.id,
            title: blog.heading || 'Untitled',
            category: blog?.category?.name || 'General',
            date: formatDate(blog.createdAt),
            author: blog.author || 'GRAAHO Team',
            image,
            excerpt: plainContent,
            createdAt: blog.createdAt || '',
          };
        });

        mappedArticles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setArticles(mappedArticles);
      } catch (error) {
        console.error('Failed to fetch news articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    getArticles();
  }, [assetBaseUrl]);

  const displayArticles = useMemo(() => articles, [articles]);

  const totalPages = useMemo(() => {
    if (displayArticles.length === 0) return 1;
    return Math.ceil(displayArticles.length / ITEMS_PER_PAGE);
  }, [displayArticles.length, ITEMS_PER_PAGE]);

  useEffect(() => {
    setCurrentPage(1);
  }, [displayArticles.length]);

  const paginatedArticles = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return displayArticles.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [displayArticles, currentPage, ITEMS_PER_PAGE]);

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <main className="w-full bg-[#f9f9f9] text-gray-900 overflow-hidden font-sans">

  {/* HEADER */}
  <section className="relative w-full pt-24 pb-36 sm:pt-32 sm:pb-48 lg:pt-40 lg:pb-56 overflow-hidden bg-secondary">
    
    <div className="absolute inset-0">
      <img 
        src={headerBg}
        className="w-full h-full object-cover"
        alt="News Header Background"
      />
      {/* Dark Overlay for Text Readability */}
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
    </div>

    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10 text-center">

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >

        <div className="mb-4 md:mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-orange-main">
          <Calendar size={13} className="md:w-3.5 md:h-3.5" />
          News & Blogs
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl  font-black text-white leading-[1.1] tracking-tighter">
          Latest <span className="text-orange-main">Insights</span>
        </h1>

        <p className="mt-4 sm:mt-6 text-[16px] lg:text-xl text-gray-300 max-w-2xl mx-auto font-medium leading-relaxed px-2 sm:px-0">
          Stay updated with our latest projects, industry news, and architectural innovations.
        </p>

      </motion.div>
    </div>
  </section>

  {/* ARTICLES GRID / SCROLLER */}
  <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 -mt-20 md:-mt-32 relative z-20 pb-12 md:pb-24">

    <div className="flex overflow-x-auto sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 pb-8 sm:pb-0 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] -mx-4 px-4 sm:mx-0 sm:px-0">

      {loading ? (
        <p className="text-center col-span-full text-gray-500 font-medium">
          Loading articles...
        </p>
      ) : paginatedArticles && paginatedArticles.length > 0 ? (
        paginatedArticles.map((article, idx) => (
          <ArticleCard key={article.id || idx} article={article} idx={idx} />
        ))
      ) : (
        <p className="text-center col-span-full text-gray-500">
          No articles found on this page.
        </p>
      )}

    </div>

    {!loading && displayArticles.length > 0 && (
      <div className="mt-10 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className={`rounded-xl px-5 py-2.5 text-sm font-semibold border transition-colors duration-300 ${
            currentPage === 1
              ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-100'
              : 'border-primary text-title hover:bg-primary hover:text-white'
          }`}
        >
          Previous
        </button>

        <span className="text-sm font-semibold text-gray-600">
          Page {currentPage} of {totalPages}
        </span>

        <button
          type="button"
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={`rounded-xl px-5 py-2.5 text-sm font-semibold border transition-colors duration-300 ${
            currentPage === totalPages
              ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-100'
              : 'border-primary text-title hover:bg-primary hover:text-white'
          }`}
        >
          Next
        </button>
      </div>
    )}

  </section>

  {/* NEWSLETTER */}


</main>
  );
};

export default NewsMedia;
